import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ImportDataService } from '../../services/import-data.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import { SessionContextService } from 'app/core/helpers/session-context.helper';
import { Subject, takeUntil, Observable, startWith, map } from 'rxjs';

interface MasterData {
  key: string;
  label: string;
  selected: boolean;
}

interface Tenant {
  id: string;
  tenantName: string;
  company_name?: string;
}

interface Store {
  id: string;
  storeName: string;
  code?: string;
  tenantId: string;
}

@Component({
  selector: 'app-import-data-panel',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
  ],
  templateUrl: './import-data-panel.component.html',
  styleUrls: ['./import-data-panel.component.scss'],
})
export class ImportDataPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  isImporting = false;
  importProgress = 0;

  // Filters
  filterByTenant = false;
  filterByStore = false;

  // Form Controls
  tenantControl = new FormControl();
  storeControl = new FormControl();

  // Data
  allTenants: Tenant[] = [];
  allStores: Store[] = [];
  filteredTenants$: Observable<Tenant[]>;
  filteredStores$: Observable<Store[]>;

  masters: MasterData[] = [
    { key: 'items', label: 'Items', selected: false },
    { key: 'companies', label: 'Company', selected: false },
    { key: 'companyGroup', label: 'Company Group', selected: false },
    { key: 'hsnCodes', label: 'HSN Codes', selected: false },
    { key: 'categories', label: 'Categories', selected: false },
    { key: 'suppliers', label: 'Suppliers', selected: false },
    { key: 'customers', label: 'Customers', selected: false },
  ];

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private importDataService: ImportDataService,
    private http: HttpClient,
    private sessionContext: SessionContextService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.importDataService.isImportPanelOpen$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((isOpen) => {
        this.isOpen = isOpen;
        if (isOpen) {
          this.loadTenants();
          this.loadStores();
        }
      });

    // Setup autocomplete filtering for tenants
    this.filteredTenants$ = this.tenantControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTenants(value)),
    );

    // Setup autocomplete filtering for stores
    this.filteredStores$ = this.storeControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterStores(value)),
    );
  }

  /**
   * Load tenants from API
   */
  private loadTenants(): void {
    this.http
      .get<Tenant[]>(`${environment.apiUrl}/tenant-master`)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (tenants) => {
          this.allTenants = tenants;
        },
        error: (error) => {
          console.error('Error loading tenants:', error);
          this.snackBar.open('Failed to load tenants', 'Close', { duration: 3000 });
        },
      });
  }

  /**
   * Load stores from API
   */
  private loadStores(): void {
    this.http
      .get<Store[]>(`${environment.apiUrl}/stores`)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (stores) => {
          this.allStores = stores;
        },
        error: (error) => {
          console.error('Error loading stores:', error);
          this.snackBar.open('Failed to load stores', 'Close', { duration: 3000 });
        },
      });
  }

  /**
   * Filter tenants for autocomplete
   */
  private _filterTenants(value: string | Tenant): Tenant[] {
    if (!value) {
      return this.allTenants;
    }

    const filterValue =
      typeof value === 'string' ? value.toLowerCase() : value.tenantName.toLowerCase();
    return this.allTenants.filter((tenant) =>
      tenant.tenantName.toLowerCase().includes(filterValue),
    );
  }

  /**
   * Filter stores for autocomplete
   */
  private _filterStores(value: string | Store): Store[] {
    if (!value) {
      return this.allStores;
    }

    const filterValue =
      typeof value === 'string' ? value.toLowerCase() : value.storeName.toLowerCase();
    return this.allStores.filter((store) => store.storeName.toLowerCase().includes(filterValue));
  }

  /**
   * Display function for tenant autocomplete
   */
  displayTenantFn = (tenant: Tenant): string => {
    return tenant && tenant.tenantName ? tenant.tenantName : '';
  };

  /**
   * Display function for store autocomplete
   */
  displayStoreFn = (store: Store): string => {
    return store && store.storeName ? store.storeName : '';
  };

  /**
   * Handle tenant filter checkbox change
   */
  onTenantFilterChange(): void {
    if (!this.filterByTenant) {
      this.tenantControl.reset();
    }
  }

  /**
   * Handle store filter checkbox change
   */
  onStoreFilterChange(): void {
    if (!this.filterByStore) {
      this.storeControl.reset();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Toggle select all
   */
  toggleSelectAll(): void {
    const allSelected = this.masters.every((m) => m.selected);
    this.masters.forEach((m) => (m.selected = !allSelected));
  }

  /**
   * Check if all masters are selected
   */
  get allSelected(): boolean {
    return this.masters.every((m) => m.selected);
  }

  /**
   * Check if some (but not all) masters are selected
   */
  get someSelected(): boolean {
    const selected = this.masters.filter((m) => m.selected).length;
    return selected > 0 && selected < this.masters.length;
  }

  /**
   * Check if import button should be enabled
   */
  get canImport(): boolean {
    return this.masters.some((m) => m.selected) && !this.isImporting;
  }

  /**
   * Close the panel
   */
  close(): void {
    if (!this.isImporting) {
      this.importDataService.closeImportPanel();
      // Reset selections
      this.masters.forEach((m) => (m.selected = false));
    }
  }

  /**
   * Import selected masters
   */
  async importData(): Promise<void> {
    const selectedMasters = this.masters.filter((m) => m.selected);

    if (selectedMasters.length === 0) {
      this.snackBar.open('Please select at least one master to import', 'Close', {
        duration: 3000,
      });
      return;
    }

    // Get source tenant and store
    const sourceTenantId =
      this.filterByTenant && this.tenantControl.value?.id ? this.tenantControl.value.id : null;
    const sourceStoreId =
      this.filterByStore && this.storeControl.value?.id ? this.storeControl.value.id : null;

    // Get destination (current user's tenant and store)
    const destTenantId = this.sessionContext.getTenantId();
    const destStoreId = this.sessionContext.getStoreId();

    this.isImporting = true;
    this.importProgress = 0;

    try {
      const totalSteps = selectedMasters.length;
      let completedSteps = 0;

      for (const master of selectedMasters) {
        // Import each master from source to destination
        await this.importMaster(
          master.key,
          sourceTenantId,
          sourceStoreId,
          destTenantId!,
          destStoreId!,
        );

        completedSteps++;
        this.importProgress = (completedSteps / totalSteps) * 100;
      }

      let message = `Successfully imported ${selectedMasters.length} master(s)!`;
      if (sourceTenantId || sourceStoreId) {
        message += ` from ${sourceTenantId ? 'selected tenant' : ''}${sourceTenantId && sourceStoreId ? ' and ' : ''}${sourceStoreId ? 'selected store' : ''}`;
      }

      this.snackBar.open(message, 'Close', {
        duration: 5000,
        panelClass: ['snackbar-success'],
      });

      // Close panel after success
      setTimeout(() => {
        this.isImporting = false;
        this.importProgress = 0;
        this.close();
      }, 1500);
    } catch (error) {
      console.error('Import error:', error);
      this.snackBar.open('Error importing data. Please try again.', 'Close', {
        duration: 5000,
        panelClass: ['snackbar-error'],
      });
      this.isImporting = false;
      this.importProgress = 0;
    }
  }

  /**
   * Import a single master
   */
  private async importMaster(
    masterKey: string,
    sourceTenantId: string | null,
    sourceStoreId: string | null,
    destTenantId: string,
    destStoreId: string,
  ): Promise<void> {
    // This is a placeholder - you'll need to implement the actual import logic
    // For now, we'll just simulate the import
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 1000);
    });
  }
}
